/**
 * LOGOS HealthKit Bridge — Watch Biometrics → 7-Layer Engine Integration
 * 
 * Maps Apple Watch sensor data to:
 * - Levin Signals (8 new biometric signals)
 * - Free Energy modulators (SpO₂, HR Recovery)
 * - Watson landscape shifts (VO₂, temp, RHR trend, steps trend)
 * - Hoffman bias detectors (4 incongruence detectors)
 * - Penrose coherence modulators (noise exposure)
 * - Policy overrides (ECG AFib, SpO₂ critical → PAUSA)
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @version 1.0.0
 */

import type { WatchBiometrics, ECGData } from '../plugins/logos-healthkit';
import { clamp } from '../utils/math';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export interface WatchLevinSignal {
  id: string;
  label: string;
  severity: number;    // 0-1
  source: string;      // which sensor triggered it
  description: string;
}

export interface WatchHoffmanBias {
  id: string;
  label: string;
  magnitude: number;   // 0-1
  subjectiveValue: number;
  objectiveValue: number;
  description: string;
}

export interface WatchFreeEnergyMod {
  spo2Mod: number;           // -0.15 to +0.05
  hrRecoveryMod: number;     // -0.10 to +0.05
  totalMod: number;          // sum
}

export interface WatchWatsonShift {
  domain: string;
  shift: number;             // -0.15 to +0.10
  source: string;
}

export interface WatchCoherenceMod {
  noiseMod: number;          // -0.10 to 0
  totalMod: number;
}

export interface WatchPolicyOverride {
  active: boolean;
  verdict: string;           // 'PAUSA' if active
  reason: string;
  sources: string[];
}

export interface WatchEngineResult {
  levinSignals: WatchLevinSignal[];
  hoffmanBiases: WatchHoffmanBias[];
  freeEnergyMod: WatchFreeEnergyMod;
  watsonShifts: WatchWatsonShift[];
  coherenceMod: WatchCoherenceMod;
  policyOverride: WatchPolicyOverride;
}

// ═══════════════════════════════════════════════════════════════
// LEVIN SIGNALS — 8 biometric pathological patterns
// ═══════════════════════════════════════════════════════════════

export function watchLevinSignals(bio: WatchBiometrics, ecg: ECGData | null): WatchLevinSignal[] {
  const signals: WatchLevinSignal[] = [];

  // 1. ECG Atrial Fibrillation detected
  if (ecg?.aFibDetected) {
    signals.push({
      id: 'ecg_afib',
      label: 'Fibrilación Auricular Detectada',
      severity: 0.95,
      source: 'ECG',
      description: 'El ECG del Apple Watch detectó fibrilación auricular. Consulta a tu cardiólogo.',
    });
  }

  // 2. SpO₂ critically low (<92%)
  if (bio.spo2 > 0 && bio.spo2 < 92) {
    signals.push({
      id: 'spo2_low',
      label: 'SpO₂ Críticamente Bajo',
      severity: clamp(1.0 - (bio.spo2 - 85) / 7, 0, 1), // 85%=1.0, 92%=0.0
      source: 'SpO₂',
      description: `Saturación de oxígeno en ${bio.spo2.toFixed(0)}%. Normal: >95%.`,
    });
  }

  // 3. Body temperature anomaly (>38°C or <35.5°C)
  if (bio.bodyTemperature > 0) {
    if (bio.bodyTemperature > 38.0) {
      signals.push({
        id: 'temp_high',
        label: 'Temperatura Corporal Elevada',
        severity: clamp((bio.bodyTemperature - 38.0) / 2.0, 0, 1),
        source: 'Temperatura',
        description: `Temperatura: ${bio.bodyTemperature.toFixed(1)}°C. Posible fiebre.`,
      });
    } else if (bio.bodyTemperature < 35.5) {
      signals.push({
        id: 'temp_low',
        label: 'Temperatura Corporal Baja',
        severity: clamp((35.5 - bio.bodyTemperature) / 2.0, 0, 1),
        source: 'Temperatura',
        description: `Temperatura: ${bio.bodyTemperature.toFixed(1)}°C. Hipotermia leve.`,
      });
    }
  }

  // 4. Walking asymmetry high (>10% suggests gait issue)
  if (bio.walkingAsymmetry > 0 && bio.walkingAsymmetry > 10) {
    signals.push({
      id: 'gait_asymmetry',
      label: 'Asimetría de Marcha Elevada',
      severity: clamp((bio.walkingAsymmetry - 10) / 15, 0, 1),
      source: 'Acelerómetro/Giroscopio',
      description: `Asimetría: ${bio.walkingAsymmetry.toFixed(1)}%. Normal: <10%.`,
    });
  }

  // 5. Poor heart rate recovery (<12 bpm drop in 1 min)
  if (bio.heartRateRecovery > 0 && bio.heartRateRecovery < 12) {
    signals.push({
      id: 'hr_recovery_poor',
      label: 'Recuperación Cardíaca Deficiente',
      severity: clamp(1.0 - bio.heartRateRecovery / 12, 0, 1),
      source: 'FC Recovery',
      description: `Recovery: ${bio.heartRateRecovery.toFixed(0)} bpm/min. Ideal: >12 bpm.`,
    });
  }

  // 6. Toxic noise exposure (>85 dB sustained)
  if (bio.noiseExposure > 0 && bio.noiseExposure > 85) {
    signals.push({
      id: 'noise_toxic',
      label: 'Exposición a Ruido Tóxico',
      severity: clamp((bio.noiseExposure - 85) / 25, 0, 1),
      source: 'Micrófono Ambiente',
      description: `Nivel: ${bio.noiseExposure.toFixed(0)} dB. Riesgo auditivo >85 dB.`,
    });
  }

  // 7. Very low REM sleep (<45 min when total sleep >5h)
  if (bio.sleepHours > 5 && bio.sleepStages?.rem >= 0 && bio.sleepStages.rem < 0.75) {
    signals.push({
      id: 'rem_low',
      label: 'Sueño REM Insuficiente',
      severity: clamp(1.0 - bio.sleepStages.rem / 0.75, 0, 1),
      source: 'Sueño',
      description: `REM: ${(bio.sleepStages.rem * 60).toFixed(0)} min. Ideal: >90 min.`,
    });
  }

  // 8. Very low deep sleep (<30 min when total sleep >5h)
  if (bio.sleepHours > 5 && bio.sleepStages?.deep >= 0 && bio.sleepStages.deep < 0.5) {
    signals.push({
      id: 'deep_low',
      label: 'Sueño Profundo Insuficiente',
      severity: clamp(1.0 - bio.sleepStages.deep / 0.5, 0, 1),
      source: 'Sueño',
      description: `Deep: ${(bio.sleepStages.deep * 60).toFixed(0)} min. Ideal: >60 min.`,
    });
  }

  return signals;
}

// ═══════════════════════════════════════════════════════════════
// FREE ENERGY MODULATORS — SpO₂ + HR Recovery
// ═══════════════════════════════════════════════════════════════

export function modulateFreeEnergy(bio: WatchBiometrics): WatchFreeEnergyMod {
  let spo2Mod = 0;
  let hrRecoveryMod = 0;

  // SpO₂: <95% increases free energy (surprise), >97% slightly decreases it
  if (bio.spo2 > 0) {
    if (bio.spo2 < 95) {
      spo2Mod = clamp((95 - bio.spo2) / 10 * 0.15, 0, 0.15); // up to +0.15
    } else if (bio.spo2 > 97) {
      spo2Mod = -0.03; // slight reduction — body is well-oxygenated
    }
  }

  // HR Recovery: poor recovery increases free energy
  if (bio.heartRateRecovery > 0) {
    if (bio.heartRateRecovery < 12) {
      hrRecoveryMod = clamp((12 - bio.heartRateRecovery) / 12 * 0.10, 0, 0.10);
    } else if (bio.heartRateRecovery > 25) {
      hrRecoveryMod = -0.03; // excellent recovery
    }
  }

  return {
    spo2Mod,
    hrRecoveryMod,
    totalMod: spo2Mod + hrRecoveryMod,
  };
}

// ═══════════════════════════════════════════════════════════════
// WATSON LANDSCAPE SHIFTS — VO₂, temp, RHR, steps
// ═══════════════════════════════════════════════════════════════

export function watsonLandscapeShifts(bio: WatchBiometrics): WatchWatsonShift[] {
  const shifts: WatchWatsonShift[] = [];

  // VO₂ max affects Physical domain landscape
  if (bio.vo2Max > 0) {
    let shift = 0;
    if (bio.vo2Max >= 45) {
      shift = 0.08; // excellent fitness → raise Physical base
    } else if (bio.vo2Max >= 35) {
      shift = 0.03; // good fitness
    } else if (bio.vo2Max < 25) {
      shift = -0.10; // poor fitness → lower Physical base
    }
    if (shift !== 0) {
      shifts.push({ domain: 'Physical', shift, source: 'VO₂ Max' });
    }
  }

  // Body temperature anomaly affects Physical domain
  if (bio.bodyTemperature > 0) {
    if (bio.bodyTemperature > 37.5) {
      const shift = -clamp((bio.bodyTemperature - 37.5) / 2.5 * 0.12, 0, 0.12);
      shifts.push({ domain: 'Physical', shift, source: 'Temperatura' });
    }
  }

  // Resting heart rate trend affects Physical domain
  if (bio.restingHeartRate > 0) {
    let shift = 0;
    if (bio.restingHeartRate < 55) {
      shift = 0.05; // athletic heart
    } else if (bio.restingHeartRate > 75) {
      shift = -0.08; // elevated RHR
    }
    if (shift !== 0) {
      shifts.push({ domain: 'Physical', shift, source: 'FC Reposo' });
    }
  }

  // Step count affects Physical domain
  if (bio.stepCount > 0) {
    let shift = 0;
    if (bio.stepCount >= 10000) {
      shift = 0.05; // WHO target met
    } else if (bio.stepCount < 3000) {
      shift = -0.08; // sedentary
    }
    if (shift !== 0) {
      shifts.push({ domain: 'Physical', shift, source: 'Pasos' });
    }
  }

  // Sleep affects Emotional domain
  if (bio.sleepHours >= 0) {
    if (bio.sleepHours < 5) {
      shifts.push({ domain: 'Emotional', shift: -0.10, source: 'Sueño insuficiente' });
    } else if (bio.sleepHours >= 7 && bio.sleepHours <= 9) {
      shifts.push({ domain: 'Emotional', shift: 0.05, source: 'Sueño óptimo' });
    }
  }

  return shifts;
}

// ═══════════════════════════════════════════════════════════════
// HOFFMAN BIAS DETECTORS — subjective vs objective incongruence
// ═══════════════════════════════════════════════════════════════

export function watchHoffmanBiases(
  bio: WatchBiometrics,
  subjectiveState: Record<string, number>,
): WatchHoffmanBias[] {
  const biases: WatchHoffmanBias[] = [];

  // 1. Sleep Illusion: user reports high sleep but Watch measured <5h
  if (bio.sleepHours >= 0 && bio.sleepHours < 5 && (subjectiveState.sleep ?? 0) > 0.7) {
    biases.push({
      id: 'sleep_illusion',
      label: 'Ilusión de Sueño',
      magnitude: clamp((subjectiveState.sleep ?? 0) - bio.sleepHours / 8, 0, 1),
      subjectiveValue: subjectiveState.sleep ?? 0,
      objectiveValue: clamp(bio.sleepHours / 8),
      description: `Reportas sueño alto (${((subjectiveState.sleep ?? 0) * 100).toFixed(0)}%) pero el Watch midió ${bio.sleepHours.toFixed(1)}h.`,
    });
  }

  // 2. Fitness Overestimation: user reports high exercise but Watch measured <10min
  if (bio.exerciseMinutes >= 0 && bio.exerciseMinutes < 10 && (subjectiveState.exercise ?? 0) > 0.6) {
    biases.push({
      id: 'fitness_overestimation',
      label: 'Sobreestimación de Fitness',
      magnitude: clamp((subjectiveState.exercise ?? 0) - bio.exerciseMinutes / 30, 0, 1),
      subjectiveValue: subjectiveState.exercise ?? 0,
      objectiveValue: clamp(bio.exerciseMinutes / 30),
      description: `Reportas ejercicio alto pero el Watch midió ${bio.exerciseMinutes.toFixed(0)} min.`,
    });
  }

  // 3. Energy Overestimation: user reports high energy but RHR is elevated
  if (bio.restingHeartRate > 75 && (subjectiveState.energy ?? 0) > 0.7) {
    const objective = clamp(1.0 - (bio.restingHeartRate - 40) / 40);
    biases.push({
      id: 'energy_overestimation',
      label: 'Sobreestimación de Energía',
      magnitude: clamp((subjectiveState.energy ?? 0) - objective, 0, 1),
      subjectiveValue: subjectiveState.energy ?? 0,
      objectiveValue: objective,
      description: `Reportas energía alta pero tu FC reposo es ${bio.restingHeartRate.toFixed(0)} bpm (elevada).`,
    });
  }

  // 4. Peace Incongruence: user reports high peace but HRV is very low
  if (bio.hrv > 0 && bio.hrv < 30 && (subjectiveState.peace ?? 0) > 0.7) {
    const objective = clamp((bio.hrv - 20) / 80);
    biases.push({
      id: 'peace_incongruence',
      label: 'Incongruencia de Paz Interior',
      magnitude: clamp((subjectiveState.peace ?? 0) - objective, 0, 1),
      subjectiveValue: subjectiveState.peace ?? 0,
      objectiveValue: objective,
      description: `Reportas paz alta pero tu HRV es ${bio.hrv.toFixed(0)}ms (estrés fisiológico).`,
    });
  }

  return biases;
}

// ═══════════════════════════════════════════════════════════════
// COHERENCE MODULATOR — noise exposure
// ═══════════════════════════════════════════════════════════════

export function modulateCoherence(bio: WatchBiometrics): WatchCoherenceMod {
  let noiseMod = 0;

  // Sustained noise >80dB reduces coherence
  if (bio.noiseExposure > 0 && bio.noiseExposure > 80) {
    noiseMod = -clamp((bio.noiseExposure - 80) / 30 * 0.10, 0, 0.10);
  }

  return {
    noiseMod,
    totalMod: noiseMod,
  };
}

// ═══════════════════════════════════════════════════════════════
// POLICY OVERRIDE — critical health → PAUSA
// ═══════════════════════════════════════════════════════════════

export function watchPolicyOverride(bio: WatchBiometrics, ecg: ECGData | null): WatchPolicyOverride {
  const sources: string[] = [];

  // ECG AFib → PAUSA
  if (ecg?.aFibDetected) {
    sources.push('ECG: Fibrilación auricular detectada');
  }

  // SpO₂ <90% → PAUSA
  if (bio.spo2 > 0 && bio.spo2 < 90) {
    sources.push(`SpO₂: ${bio.spo2.toFixed(0)}% — nivel crítico`);
  }

  // Very high resting HR (>120 bpm at rest)
  if (bio.restingHeartRate > 120) {
    sources.push(`FC Reposo: ${bio.restingHeartRate.toFixed(0)} bpm — taquicardia`);
  }

  const active = sources.length > 0;

  return {
    active,
    verdict: active ? 'PAUSA' : '',
    reason: active
      ? 'Señal biométrica crítica detectada. Consulta a tu médico antes de tomar decisiones importantes.'
      : '',
    sources,
  };
}

// ═══════════════════════════════════════════════════════════════
// MASTER FUNCTION — process all Watch data through engine layers
// ═══════════════════════════════════════════════════════════════

export function processWatchBiometrics(
  bio: WatchBiometrics,
  ecg: ECGData | null,
  subjectiveState: Record<string, number>,
): WatchEngineResult {
  return {
    levinSignals: watchLevinSignals(bio, ecg),
    hoffmanBiases: watchHoffmanBiases(bio, subjectiveState),
    freeEnergyMod: modulateFreeEnergy(bio),
    watsonShifts: watsonLandscapeShifts(bio),
    coherenceMod: modulateCoherence(bio),
    policyOverride: watchPolicyOverride(bio, ecg),
  };
}

// ═══════════════════════════════════════════════════════════════
// KAIRA INTEGRATION: Watch Biometrics → AIFET Precision Signals
// ═══════════════════════════════════════════════════════════════

export interface KairaWatchBridge {
  pi_interoceptive_signal: 'hyper' | 'flexible' | 'hypo' | 'unavailable';
  pi_sensory_signal: 'hyper' | 'flexible' | 'hypo' | 'unavailable';
  allostatic_load_signal: 'low' | 'moderate' | 'high' | 'critical' | 'unavailable';
  reconsolidation_window_biometric: boolean;
  confidence: number;
}

export function processWatchForKaira(bio: WatchBiometrics | null): KairaWatchBridge {
  if (!bio) {
    return {
      pi_interoceptive_signal: 'unavailable',
      pi_sensory_signal: 'unavailable',
      allostatic_load_signal: 'unavailable',
      reconsolidation_window_biometric: false,
      confidence: 0,
    };
  }

  let confidence = 0;
  let dataPoints = 0;

  // Π_interoceptive from HRV + Resting HR
  let pi_interoceptive: 'hyper' | 'flexible' | 'hypo' | 'unavailable' = 'unavailable';
  if (bio.hrv !== undefined && bio.hrv !== -1 && bio.restingHeartRate !== undefined && bio.restingHeartRate !== -1) {
    dataPoints += 2;
    confidence += 0.4;
    if (bio.hrv < 30 && bio.restingHeartRate > 75) {
      pi_interoceptive = 'hyper'; // sympathetic dominance
    } else if (bio.hrv < 40 && bio.restingHeartRate > 70) {
      pi_interoceptive = 'hypo'; // low vagal tone
    } else {
      pi_interoceptive = 'flexible';
    }
  }

  // Π_sensory from noise exposure
  let pi_sensory: 'hyper' | 'flexible' | 'hypo' | 'unavailable' = 'unavailable';
  if (bio.noiseExposure !== undefined && bio.noiseExposure !== -1) {
    dataPoints += 1;
    confidence += 0.15;
    if (bio.noiseExposure > 80) {
      pi_sensory = 'hyper'; // chronic sensory overload
    } else if (bio.noiseExposure < 50) {
      pi_sensory = 'flexible';
    } else {
      pi_sensory = 'hypo'; // moderate stress
    }
  }

  // Allostatic load from multiple signals
  let allostatic_load: 'low' | 'moderate' | 'high' | 'critical' | 'unavailable' = 'unavailable';
  const allostaticFactors: number[] = [];
  if (bio.hrv !== undefined && bio.hrv !== -1) allostaticFactors.push(bio.hrv < 30 ? 1 : bio.hrv < 50 ? 0.5 : 0);
  if (bio.restingHeartRate !== undefined && bio.restingHeartRate !== -1) allostaticFactors.push(bio.restingHeartRate > 80 ? 1 : bio.restingHeartRate > 70 ? 0.5 : 0);
  if (bio.sleepHours !== undefined && bio.sleepHours !== -1) allostaticFactors.push(bio.sleepHours < 6 ? 1 : bio.sleepHours < 7 ? 0.5 : 0);
  if (bio.spo2 !== undefined && bio.spo2 !== -1) allostaticFactors.push(bio.spo2 < 94 ? 1 : bio.spo2 < 96 ? 0.5 : 0);

  if (allostaticFactors.length > 0) {
    dataPoints += allostaticFactors.length;
    confidence += 0.3;
    const avgLoad = allostaticFactors.reduce((a, b) => a + b, 0) / allostaticFactors.length;
    if (avgLoad > 0.75) allostatic_load = 'critical';
    else if (avgLoad > 0.50) allostatic_load = 'high';
    else if (avgLoad > 0.25) allostatic_load = 'moderate';
    else allostatic_load = 'low';
  }

  // Reconsolidation window: HRV > 60 + RHR < 65 + sleep > 7.5h
  const reconsolidation_window_biometric =
    (bio.hrv ?? -1) > 60 &&
    (bio.restingHeartRate ?? 100) < 65 &&
    (bio.sleepHours ?? -1) > 7.5;

  if (reconsolidation_window_biometric) {
    confidence += 0.15;
  }

  return {
    pi_interoceptive_signal: pi_interoceptive,
    pi_sensory_signal: pi_sensory,
    allostatic_load_signal: allostatic_load,
    reconsolidation_window_biometric,
    confidence: Math.min(1.0, confidence),
  };
}
